import Component from '@glimmer/component'
import { service } from '@ember/service'
import { format } from 'date-fns'
import { type IntlService, t } from 'ember-intl'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { BaseTableActions } from 'teacher-ui-client/components/base/table-actions'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import SelectableCell from 'teacher-ui-client/components/stable-table/basic-cells/selectable'
import { and, not, or } from 'ember-truth-helpers'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import type { SortComparerKey, SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import type { SafeString } from '@ember/template'
import type { RowId } from 'teacher-ui-client/components/stable-table/basic-table/cell'

interface Args {
  schoolClasses: SchoolClass[]
  isLoadingData?: boolean
  sortingConfig?: SortingConfig
  paginationConfig?: PaginationConfig
  selectedItemIds?: string[]
  onSortClick?: (sortingConfig: SortingConfig) => void
  onSelectClick?: (selectedIds: RowId[]) => void
}

interface Signature {
  Args: Args
}

interface Column {
  value?: string | SafeString
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SelectableCell | typeof SortableCell
  cellComponent?: typeof SelectableCell
}

export class RolloverClassSelectionTable extends Component<Signature> {
  @service intl!: IntlService

  get schoolClassItems() {
    const createdAtFormat = this.intl.t('dateFormats.dateFns.d MMM yyyy')
    return this.args.schoolClasses.map(({ id, name, studentCount, teacherNames, createdAt }) => {
      return {
        id, // for checkbox selection
        className: name,
        studentCount,
        teacherNames: teacherNames.join(', '),
        createdAt,
        createdAtFormatted: format(createdAt, createdAtFormat),
      }
    })
  }

  get columns(): Column[] {
    const { intl } = this
    const keyPrefix = 'manage.rollover.steps.delete_classes.schoolClassesTable'

    const headerClassNames = 'text-white font-bold bg-dusty-black-250'
    const cellClassNames = 'border-t border-dusty-black-100'
    const defaultColumn = {
      classNames: `${headerClassNames} p-3`,
      cellClassNames: `${cellClassNames} p-3`,
    }
    // Select column needs to move the padding around a bit, to make the entire cell a clickable area.
    const selectColumn = {
      classNames: `${headerClassNames} w-px`,
      componentClassNames: 'p-3',
      cellClassNames,
      cellComponentClassNames: 'p-3',
    }

    return [
      {
        ...selectColumn,
        component: SelectableCell,
        cellComponent: SelectableCell,
      },
      {
        ...defaultColumn,
        value: intl.t(`${keyPrefix}.fields.className`),
        valuePath: 'className',
        classNames: `${defaultColumn.classNames} w-1/3`,
        component: SortableCell,
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${keyPrefix}.fields.studentCount`),
        classNames: `${defaultColumn.classNames} border-l border-white`,
        valuePath: 'studentCount',
        component: SortableCell,
        comparerKey: 'sortByNumber',
      },
      {
        ...defaultColumn,
        value: intl.t(`${keyPrefix}.fields.teachers`),
        classNames: `${defaultColumn.classNames} border-l border-white w-1/4`,
        valuePath: 'teacherNames',
        component: SortableCell,
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${keyPrefix}.fields.createdAt`),
        classNames: `${defaultColumn.classNames} border-l border-white`,
        sortKey: 'createdAt',
        valuePath: 'createdAtFormatted',
        component: SortableCell,
        comparerKey: 'sortByDate',
      },
    ]
  }

  <template>
    <BaseTableActions
      @items={{this.schoolClassItems}}
      @columns={{this.columns}}
      @selectedItemIds={{@selectedItemIds}}
      @sortingConfig={{@sortingConfig}}
      @paginationConfig={{@paginationConfig}}
      @onSortClick={{@onSortClick}}
      @onSelectClick={{@onSelectClick}}
      as |Table state paging|
    >
      <Table class="border-dusty-black-100 w-full border" as |tbl|>
        <tbl.head />
        {{#if (and state.isEmptyAndIdle (not @isLoadingData))}}
          <tbl.spanner class="p-3 text-center italic">
            {{t "manage.rollover.steps.delete_classes.schoolClassesTable.emptyMessage"}}
          </tbl.spanner>
        {{else}}
          {{#if (or state.isTransforming @isLoadingData)}}
            <tbl.spanner
              class="p-3 text-center {{unless state.isEmpty 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </tbl.spanner>
          {{/if}}
          <tbl.body class={{if state.isTransforming "opacity-50"}} />
        {{/if}}
      </Table>
      {{#if paging.pageCount}}
        <BasePageSelector class="mt-4" @pageCount={{paging.pageCount}} @itemCount={{this.schoolClassItems.length}} />
      {{/if}}
    </BaseTableActions>
  </template>
}
export default RolloverClassSelectionTable
